import {
  aiRulesCheckCommand,
  aiRulesCommand,
  aiRulesInstallCommand,
  aiRulesUpdateCommand,
} from "./commands/ai-rules.js";
import { bisectCommand } from "./commands/bisect.js";
import { doctorCommand } from "./commands/doctor.js";
import {
  examplesCopyCommand,
  examplesListCommand,
} from "./commands/examples.js";
import { explainCommand } from "./commands/explain.js";
import { graphCommand } from "./commands/graph.js";
import { initCommand } from "./commands/init.js";
import { inspectCommand } from "./commands/inspect.js";
import { newModuleCommand, newOrchestratorCommand } from "./commands/new.js";
import { replayUnderCommand } from "./commands/replay-under.js";
import { replayCommand } from "./commands/replay.js";
import { rulesDiffCommand } from "./commands/rules-diff.js";
import { timelineDiffCommand } from "./commands/timeline-diff.js";
import { tuneCommand } from "./commands/tune.js";
import { CLI_NAME } from "./lib/constants.js";

const HELP = `
${CLI_NAME} — CLI tools for Directive

Usage: ${CLI_NAME} <command> [options]

Commands:
  init                          Project scaffolding wizard
  new module <name>             Generate a module file
  new orchestrator <name>       Generate an AI orchestrator
  inspect <file>                Runtime system introspection
  explain <file> [req-id]       Explain why a requirement exists
  graph <file>                  Visual dependency graph
  doctor                        Health check for project setup
  install                       One-shot: drop AI rules for every
                                supported assistant (Cursor, Copilot,
                                Windsurf, Cline, Codex, Claude). No
                                prompts. Alias for ai-rules install.
  ai-rules install              Same as above (non-interactive)
  ai-rules init                 Interactive install (prompts per tool)
  ai-rules update               Refresh rules to latest version
  ai-rules check                Validate rules are current (CI)
  examples list                 Browse available examples
  examples copy <name>          Extract example to project
  replay <timeline.json>        Replay a serialized Directive timeline
                                (paired with @directive-run/timeline)
  tune --history ...            Grid-search a predicate template's $hole
                                parameters against recorded history
  rules-diff --before ...       Structural diff between two whenSpec
                                snapshots — added/removed clauses,
                                relaxed/tightened thresholds
  replay-under --history ...    Predicate backtest — diff a proposed
                                predicate against recorded history
  bisect <timeline.json>        Binary-search a timeline for the first
                                frame that triggers a failing assertion
  timeline diff <a> <b>         Semantic causal-graph diff between two
                                serialized timelines

Options:
  --help, -h                    Show this help message
  --version, -v                 Show version

init options:
  --template <name>             Template: counter, auth-flow, ai-orchestrator
  --dir <path>                  Target directory (default: cwd)
  --no-interactive              Skip prompts, use defaults

new module options:
  --with <sections>             Sections: derive,events,constraints,resolvers,effects
  --minimal                     Schema + init only
  --dir <path>                  Target directory (default: cwd)

inspect options:
  --json                        Output as JSON
  --module <name>               Inspect specific module

graph options:
  --ascii                       Terminal-only output
  --no-open                     Don't open in browser
  --output <path>               Output file path

install / ai-rules install options:
  --force                       Overwrite existing files (default: merge
                                Directive section when present, otherwise
                                skip files without a Directive section)
  --dir <path>                  Target directory (default: cwd)

ai-rules init options:
  --force                       Overwrite existing files without asking
  --merge                       Use section markers to update only the Directive section
  --tool <name>                 Skip detection, install for specific tool(s)
  --dir <path>                  Target directory (default: cwd)

examples options:
  --filter <category>           Filter by category or name
  --dest <dir>                  Destination directory for copy
`.trim();

async function main() {
  const args = process.argv.slice(2);

  // Only treat `--help` / `-h` as a request for *global* help when there is
  // no command, or the first arg is itself the help flag. When a command is
  // present (`directive replay-under --help`), routing wins and the command
  // prints its own help — otherwise a command's `-h` alias and its own
  // `--help` handling would be unreachable.
  const first = args[0];
  const wantsGlobalHelp =
    args.length === 0 || first === "--help" || first === "-h";
  if (wantsGlobalHelp) {
    console.log(HELP);
    process.exit(0);
  }

  if (first === "--version" || first === "-v") {
    const { readFileSync } = await import("node:fs");
    const { fileURLToPath } = await import("node:url");
    const { dirname, join } = await import("node:path");

    const __dirname = dirname(fileURLToPath(import.meta.url));
    const pkg = JSON.parse(
      readFileSync(join(__dirname, "..", "package.json"), "utf-8"),
    );

    console.log(pkg.version);
    process.exit(0);
  }

  const command = args[0];

  switch (command) {
    case "init": {
      await initCommand(args.slice(1));
      break;
    }

    case "new": {
      const subcommand = args[1];
      const name = args[2];

      if (subcommand === "module") {
        if (!name) {
          console.error("Usage: directive new module <name>");
          process.exit(1);
        }
        await newModuleCommand(name, args.slice(3));
      } else if (subcommand === "orchestrator") {
        if (!name) {
          console.error("Usage: directive new orchestrator <name>");
          process.exit(1);
        }
        await newOrchestratorCommand(name, args.slice(3));
      } else {
        console.error(
          `Unknown subcommand: ${subcommand ?? "(none)"}\n` +
            `Usage: ${CLI_NAME} new module <name>\n` +
            `       ${CLI_NAME} new orchestrator <name>`,
        );
        process.exit(1);
      }
      break;
    }

    case "inspect": {
      await inspectCommand(args.slice(1));
      break;
    }

    case "explain": {
      await explainCommand(args.slice(1));
      break;
    }

    case "graph": {
      await graphCommand(args.slice(1));
      break;
    }

    case "doctor": {
      await doctorCommand(args.slice(1));
      break;
    }

    case "install": {
      // Top-level alias for `ai-rules install` — matches TanStack's
      // `npx @tanstack/intent install` UX. One command, every detected
      // assistant gets its rules file.
      await aiRulesInstallCommand(args.slice(1));
      break;
    }

    case "ai-rules": {
      const subcommand = args[1];

      if (subcommand === "init") {
        await aiRulesCommand(args.slice(2));
      } else if (subcommand === "install") {
        await aiRulesInstallCommand(args.slice(2));
      } else if (subcommand === "update") {
        await aiRulesUpdateCommand(args.slice(2));
      } else if (subcommand === "check") {
        await aiRulesCheckCommand(args.slice(2));
      } else {
        console.error(
          `Unknown subcommand: ${subcommand ?? "(none)"}\n` +
            `Usage: ${CLI_NAME} ai-rules install\n` +
            `       ${CLI_NAME} ai-rules init\n` +
            `       ${CLI_NAME} ai-rules update\n` +
            `       ${CLI_NAME} ai-rules check`,
        );
        process.exit(1);
      }
      break;
    }

    case "examples": {
      const subcommand = args[1];

      if (subcommand === "list") {
        await examplesListCommand(args.slice(2));
      } else if (subcommand === "copy") {
        const name = args[2];
        if (!name) {
          console.error("Usage: directive examples copy <name>");
          process.exit(1);
        }
        await examplesCopyCommand(name, args.slice(3));
      } else {
        console.error(
          `Unknown subcommand: ${subcommand ?? "(none)"}\n` +
            `Usage: ${CLI_NAME} examples list [--filter <category>]\n` +
            `       ${CLI_NAME} examples copy <name> [--dest <dir>]`,
        );
        process.exit(1);
      }
      break;
    }

    case "replay": {
      await replayCommand(args.slice(1));
      break;
    }

    case "replay-under": {
      await replayUnderCommand(args.slice(1));
      break;
    }

    case "tune": {
      await tuneCommand(args.slice(1));
      break;
    }

    case "rules-diff": {
      await rulesDiffCommand(args.slice(1));
      break;
    }

    case "bisect": {
      await bisectCommand(args.slice(1));
      break;
    }

    case "timeline": {
      const subcommand = args[1];
      if (subcommand === "diff") {
        await timelineDiffCommand(args.slice(2));
      } else {
        console.error(
          `Unknown subcommand: ${subcommand ?? "(none)"}\n` +
            `Usage: ${CLI_NAME} timeline diff <a.json> <b.json>`,
        );
        process.exit(1);
      }
      break;
    }

    default:
      console.error(
        `Unknown command: ${command}\nRun '${CLI_NAME} --help' for usage.`,
      );
      process.exit(1);
  }
}

main().catch((err) => {
  console.error(err.message || err);
  process.exit(1);
});
